import React from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { register as registerUser } from '../features/authSlice';
import { Link, useNavigate } from 'react-router-dom';
// import AuthLeftPanel from '../components/AuthLeftPanel';
import BirlanuLogo from '../assets/images/birlanu_water_tank_logo.svg';
import CKALogo from '../assets/images/CKA Birla_Logo.svg';
import eyeIcon from '../assets/images/eye.svg';
import eyeShowIcon from '../assets/images/eye_show.svg';

const schema = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .matches(/^[A-Za-z]+$/, 'First name must contain only alphabets'),
  lastName: yup
    .string()
    .required('Last name is required')
    .matches(/^[A-Za-z]+$/, 'Last name must contain only alphabets'),
  email: yup.string().email().required('Email is required'),
  password: yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).+$/, 'Password must contain at least 1 uppercase letter, 1 number, and 1 special character'),
  confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match').required('Confirm your password'),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^\d{10}$/, 'Phone number must be 10 digits')
    .test('is-numeric', 'Phone number must be numeric', value => /^\d+$/.test(value)),
});

const Registration = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.auth);
  const { register, handleSubmit, formState: { errors } } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    // Remove confirmPassword and add role, change firstName/lastName to firstname/lastname
    const { confirmPassword, firstName, lastName, ...rest } = data;
    const payload = {
      ...rest,
      firstname: firstName,
      lastname: lastName,
      role: 'ADMIN',
    };
    const resultAction = await dispatch(registerUser(payload));

    if (registerUser.rejected.match(resultAction)) {
      toast.error(resultAction.payload || 'Registration failed');
    } else {
      toast.success('Registration successful! Please login.');
      setTimeout(() => {
        navigate('/login');
      }, 1200);
    }
  };

  return (
    <div className="login-wrapper">
      <div>
        <div className="login-logo">
          <img src={BirlanuLogo} alt="Birlanu Water Tank Logo" />
        </div>
        <div className="login-form">
          <h1>Sign Up!</h1>
          <h3>Create your account to get started</h3>
          <form className="form-info" onSubmit={handleSubmit(onSubmit)}>
            <div className="name-wrap">
              <div className='firstName-wrap'>
                <label htmlFor="firstName" className="form-label">First Name</label>
                <input
                  id="firstName"
                  {...register('firstName')}
                  className="form-input"
                  placeholder="Enter your first name"
                  style={{ borderColor: errors.firstName ? '#D7184D' : '' }}
                  inputMode="text"
                  pattern="[A-Za-z]+"
                  onInput={e => {
                    e.target.value = e.target.value.replace(/[^A-Za-z]/g, '');
                  }}
                />
                {errors.firstName && <span className="login-input-error">{errors.firstName.message}</span>}
              </div>
              <div className='lastName-wrap'>
                <label htmlFor="lastName" className="form-label">Last Name</label>
                <input
                  id="lastName"
                  {...register('lastName')}
                  className="form-input"
                  placeholder="Enter your last name"
                  style={{ borderColor: errors.lastName ? '#D7184D' : '' }}
                  inputMode="text"
                  pattern="[A-Za-z]+"
                  onInput={e => {
                    e.target.value = e.target.value.replace(/[^A-Za-z]/g, '');
                  }}
                />
                {errors.lastName && <span className="login-input-error">{errors.lastName.message}</span>}
              </div>
            </div>
            <label htmlFor="phone" className="form-label">Phone Number</label>
            <input
              id="phone"
              {...register('phone')}
              className="form-input"
              style={{ marginBottom: errors.phone ? '4px' : '', borderColor: errors.phone ? '#D7184D' : '' }}
              placeholder="Enter your phone number"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              onInput={e => {
                e.target.value = e.target.value.replace(/[^0-9]/g, '');
              }}
            />
            {errors.phone && <span className="login-input-error">{errors.phone.message}</span>}

            <label htmlFor="email" className="form-label">Email</label>
            <input id="email" {...register('email')} className="form-input" style={{ marginBottom: errors.email ? '4px' : '', borderColor: errors.email ? '#D7184D' : '' }} placeholder="Enter your email" />
            {errors.email && <span className="login-input-error">{errors.email.message}</span>}
            <div className="name-wrap">
              <div className='password-wrap'>
                <label htmlFor="password" className="form-label">Password</label>
                <div className="password-section">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    {...register('password')}
                    className="form-input mb-0"
                    style={{
                      marginBottom: errors.password ? '4px' : '',
                      borderColor: errors.password ? '#D7184D' : '',
                    }}
                    placeholder="Enter your password"
                    onKeyDown={e => {
                      if (e.key === ' ') {
                        e.preventDefault();
                      }
                    }}
                    onInput={e => {
                      if (e.target.value.includes(' ')) {
                        e.target.value = e.target.value.replace(/\s/g, '');
                      }
                    }}
                  />
                  <span
                    className="ico-eye"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label="Toggle password visibility"
                    role="button"
                    tabIndex={0}
                  >
                    <img
                      src={showPassword ? eyeShowIcon : eyeIcon}
                      alt={showPassword ? 'Show password' : 'Hide password'}
                      style={{
                        width: 24,
                        height: 24,
                        opacity: 1,
                      }}
                    />
                  </span>
                </div>
                {/* <label htmlFor="password" className="form-label">Password</label>
            <input id="password" {...register('password')} className="form-input" type="password" style={{marginBottom: errors.password ? '4px' : '', borderColor: errors.password ? '#D7184D' : ''}} placeholder="Enter your password" /> */}
                {errors.password && <span className="login-input-error">{errors.password.message}</span>}
              </div>
              <div className='confirmPassword-wrap'>
                <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                <div className="password-section">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    {...register('confirmPassword')}
                    className="form-input mb-0"
                    style={{
                      marginBottom: errors.confirmPassword ? '4px' : '',
                      borderColor: errors.confirmPassword ? '#D7184D' : '',
                    }}
                    placeholder="Confirm your password"
                    onKeyDown={e => {
                      if (e.key === ' ') {
                        e.preventDefault();
                      }
                    }}
                    onInput={e => {
                      if (e.target.value.includes(' ')) {
                        e.target.value = e.target.value.replace(/\s/g, '');
                      }
                    }}
                  />
                  <span
                    className="ico-eye"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label="Toggle confirm password visibility"
                    role="button"
                    tabIndex={0}
                  >
                    <img
                      src={showConfirmPassword ? eyeShowIcon : eyeIcon}
                      alt={showConfirmPassword ? 'Show confirm password' : 'Hide confirm password'}
                      style={{
                        width: 24,
                        height: 24,
                        opacity: 1,
                      }}
                    />
                  </span>
                </div>
                {errors.confirmPassword && <span className="login-input-error">{errors.confirmPassword.message}</span>}
              </div>
            </div>
            <div className="password-info-text">
              At least 8 characters with 1 uppercase, 1 number, and 1 special character
            </div>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          <div className="login-divider">
            <span>Or</span>
          </div>
          <div className="login-register">
            <span>Already have an account? </span>
            <Link to="/login" className="login-register-link">
              Sign In
            </Link>
          </div>
        </div>
      </div>
      <div className="footer-section">
        <img src={CKALogo} alt="CKA Birla Logo" />
        <div className="footer-links">
          <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link>
          <span>&nbsp;|&nbsp;</span>
          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
        </div>
      </div>
    </div>


  );
};

export default Registration;
